import { useMemo } from 'react';
import { triangulateVectors, getPlotCoordinates, getRequiredStarSignalCount } from '../helpers';

// Placeholder scale returned in automatic mode before data has loaded
const INITIAL_AUTO_SCALE = 0.2;
// 10% headroom added to the largest value when auto-scaling
const AUTO_SCALE_PADDING = 1.1;

export function useStarScale({ filteredData, configuration, series, vizSize, data, inputMode }) {
  return useMemo(() => {
    if (filteredData.length === 0) {
      if (configuration.scale?.scaleMode === 'Automatic') {
        // Initial value for automatic to scale off once data is loaded
        return [INITIAL_AUTO_SCALE];
      }

      if (inputMode === 'cartesian') {
        const manualMax = configuration.scale?.value || 1;
        return [vizSize / 2 / manualMax, manualMax];
      }

      const vectorMax = -triangulateVectors({
        M1: configuration.scale?.value || 1,
        M2: 0,
        M3: 0,
      })[1];
      return [vizSize / 2 / (vectorMax || 1)];
    }

    // Variable to put largest coordinate into
    let hold = 0;

    // Mapping over data to find the largest absolute coordinate
    if (series.length >= getRequiredStarSignalCount(inputMode)) {
      for (const d of filteredData) {
        const c = getPlotCoordinates({ dataPoint: d, configuration, series, inputMode });
        const l = Math.max(Math.abs(Number.parseFloat(c[0])), Math.abs(Number.parseFloat(c[1])));
        if (l > hold) {
          hold = l;
        }
      }
    }

    let largest;
    if (configuration.scale?.scaleMode === 'Automatic') {
      largest = hold * AUTO_SCALE_PADDING;
    } else if (inputMode === 'cartesian') {
      largest = configuration.scale?.value || 1;
    } else {
      largest = -triangulateVectors({ M1: configuration.scale?.value || 1, M2: 0, M3: 0 })[1];
    }

    const distributionToEdge = vizSize / 2;

    const scaleFactor = distributionToEdge / (largest || 1);

    return [scaleFactor, largest];
  }, [data, configuration.scale, vizSize, inputMode]);
}
